import { useState } from 'react';
import { Box, Typography, Alert, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, ToggleButtonGroup, ToggleButton } from '@mui/material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { CapacitySnapshot } from '../types/capacity.types';
import { CoverageMetricType } from '../types/metrics.types';
import CoverageHeatmap from '../components/CoverageHeatmap';
import CoverageCalendarView from '../components/CoverageCalendarView';

interface CoverageMonitorViewProps {
  snapshot: CapacitySnapshot;
}

export default function CoverageMonitorView({ snapshot }: CoverageMonitorViewProps) {
  const [daysToShow, setDaysToShow] = useState<number>(30);
  const [metricType, setMetricType] = useState<CoverageMetricType>('visitCapacity');
  const [viewMode, setViewMode] = useState<'heatmap' | 'calendar'>('heatmap');

  const handleDaysChange = (event: SelectChangeEvent) => {
    setDaysToShow(Number(event.target.value));
  };

  const handleMetricChange = (event: SelectChangeEvent) => {
    setMetricType(event.target.value as CoverageMetricType);
  };

  const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: 'heatmap' | 'calendar' | null) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const getMetricDescription = () => {
    switch (metricType) {
      case 'totalCapacity':
        return 'Shows total available capacity for each role per day.';
      case 'avgContinuousTime':
        return 'Shows average size of continuous time blocks available between visits. Useful for assessing if gaps are large enough for meaningful work.';
      case 'visitCapacity':
        return 'Shows how many standard visits (60 min + 15 min drive time) could fit in the available capacity.';
      default:
        return '';
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Coverage
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Forward-looking view of staffing gaps and coverage by role
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        {getMetricDescription()} Hover over cells to see detailed provider breakdowns.
      </Alert>

      {/* Controls */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
          sx={{ bgcolor: 'white' }}
        >
          <ToggleButton value="heatmap">
            <ViewModuleIcon sx={{ mr: 1 }} />
            Heatmap
          </ToggleButton>
          <ToggleButton value="calendar">
            <CalendarMonthIcon sx={{ mr: 1 }} />
            Calendar
          </ToggleButton>
        </ToggleButtonGroup>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Metric</InputLabel>
          <Select value={metricType} label="Metric" onChange={handleMetricChange}>
            <MenuItem value="totalCapacity">Total Capacity</MenuItem>
            <MenuItem value="avgContinuousTime">Avg Continuous Time</MenuItem>
            <MenuItem value="visitCapacity">Visit Capacity</MenuItem>
          </Select>
        </FormControl>

        {viewMode === 'heatmap' && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Days to Display</InputLabel>
            <Select value={daysToShow.toString()} label="Days to Display" onChange={handleDaysChange}>
              <MenuItem value="7">7 days</MenuItem>
              <MenuItem value="14">14 days</MenuItem>
              <MenuItem value="21">21 days</MenuItem>
              <MenuItem value="30">30 days</MenuItem>
            </Select>
          </FormControl>
        )}
      </Box>

      {/* View */}
      {viewMode === 'heatmap' ? (
        <CoverageHeatmap snapshot={snapshot} maxDays={daysToShow} metricType={metricType} />
      ) : (
        <CoverageCalendarView snapshot={snapshot} metricType={metricType} />
      )}
    </Box>
  );
}
